import React from "react";
import InstructionsCreateCard from "components/Notes";
import { showError } from "toast/toast";
import { OrderDiscountCodeField } from "components/orderResolve";
import {
  formatShippingChargeOrderTotalsLabel,
} from "utils/orderTotalsChargeLabels";
import { buildSpecialFeeCostBreakdownDisplay } from "utils/specialFeeCostBreakdownDisplay";
import OrderNotes from "assets/images/brands/OrderNotes.png";

export const SALES_ORDER_DOCUMENTS_COLLECTION = "sales";

/** POST multipart `upload-documents-to-order` (call after order exists). */
export async function uploadOrderDocumentsToOrder(
  token: string,
  orderDocumentId: string,
  files: File[]
): Promise<void> {
  const id = String(orderDocumentId || "").trim();
  if (!token || !id || !files.length) return;
  const fd = new FormData();
  fd.append("id", id);
  fd.append("collection_name", SALES_ORDER_DOCUMENTS_COLLECTION);
  for (const f of files) {
    fd.append("files[]", f);
  }
  const res = await fetch(`${process.env.REACT_APP_API_URL}/upload-documents-to-order`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) {
    const raw = await res.json().catch(() => ({}));
    const msg = (raw as any)?.message || (raw as any)?.error || `Upload failed (${res.status})`;
    throw new Error(typeof msg === "string" ? msg : "Upload failed");
  }
}

/** DELETE JSON `upload-documents-to-order` (server-stored attachment). */
export async function deleteOrderDocumentFromOrder(
  token: string,
  orderDocumentId: string,
  fileId: string
): Promise<void> {
  const id = String(orderDocumentId || "").trim();
  const fid = String(fileId || "").trim();
  if (!token || !id || !fid) return;
  const res = await fetch(`${process.env.REACT_APP_API_URL}/upload-documents-to-order`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id,
      collection_name: SALES_ORDER_DOCUMENTS_COLLECTION,
      file_id: fid,
    }),
  });
  if (!res.ok) {
    const raw = await res.json().catch(() => ({}));
    const msg = (raw as any)?.message || (raw as any)?.error || `Delete failed (${res.status})`;
    throw new Error(typeof msg === "string" ? msg : "Delete failed");
  }
}

export type SignageCreateOrderInstructionsAndTotalsProps = {
  notes: string;
  onNotesChange: (value: string) => void;
  orderFormResetKey: string | number;
  token: string;
  supplierId: string;
  couponInput: string;
  appliedCouponCode?: string;
  appliedCouponAmount?: number;
  voucherApplied?: boolean;
  couponResolveDisabled?: boolean;
  couponError?: string;
  onCouponInputChange: (code: string) => void;
  onCouponApply: (code: string) => void;
  onCouponClear: () => void;
  formatMoney: (n: any) => string;
  subTotalAllBeforeDiscount: number;
  canEditShipping: boolean;
  isEditingShipping: boolean;
  manualShippingAmount: string;
  onManualShippingAmountChange: (v: string) => void;
  onSaveShipping: () => void;
  onCancelEditShipping: () => void;
  onEditShipping: () => void;
  shippingAmount: number;
  specialFeeAmount: number;
  showTaxDetails: boolean;
  onToggleTaxDetails: () => void;
  totalTaxAll: number;
  grandTotal: number;
  /** Pending local files from Instructions card (synced from Notes). */
  onPendingInstructionFilesChange?: (items: { id: string; file: File }[]) => void;
  /** When user removes a server-backed attachment (has `file_id`). */
  orderDocumentIdForAttachments?: string | null;
  onServerInstructionAttachmentRemoved?: () => void;
  /** Selected shipping rows (for Order Totals label, e.g. UPS: Ground). */
  shippingOptionRows?: any[] | null;
  isUpsShipping?: boolean;
  specialFeeRows?: any[] | null;
};

/**
 * Instructions field + Order Totals card (coupon, subtotal, shipping edit, fees, tax line, grand total).
 */
export function SignageCreateOrderInstructionsAndTotals(props: SignageCreateOrderInstructionsAndTotalsProps) {
  const {
    notes,
    onNotesChange,
    orderFormResetKey,
    token,
    supplierId,
    couponInput,
    appliedCouponCode = "",
    appliedCouponAmount = 0,
    voucherApplied = false,
    couponResolveDisabled = false,
    couponError = "",
    onCouponInputChange,
    onCouponApply,
    onCouponClear,
    formatMoney: money,
    subTotalAllBeforeDiscount,
    canEditShipping,
    isEditingShipping,
    manualShippingAmount,
    onManualShippingAmountChange,
    onSaveShipping,
    onCancelEditShipping,
    onEditShipping,
    shippingAmount,
    specialFeeAmount,
    showTaxDetails,
    onToggleTaxDetails,
    totalTaxAll,
    grandTotal,
    onPendingInstructionFilesChange,
    orderDocumentIdForAttachments,
    onServerInstructionAttachmentRemoved,
    shippingOptionRows,
    isUpsShipping,
    specialFeeRows,
  } = props;

  const shippingChargeLabel = React.useMemo(
    () => formatShippingChargeOrderTotalsLabel(shippingOptionRows, { isUps: isUpsShipping }),
    [shippingOptionRows, isUpsShipping]
  );
  const specialFeeBreakdownDisplay = React.useMemo(
    () => buildSpecialFeeCostBreakdownDisplay(specialFeeRows),
    [specialFeeRows]
  );

  return (
    <div className="row mt-3 g-4 align-items-start">
      <div className="col-12 col-lg-6" id="row-overlap">
        <div className="ms-3">
          <InstructionsCreateCard
            title="Instructions"
            mode="instructions_files"
            loginType="signage"
            value={notes}
            onChange={onNotesChange}
            placeholder="Add instructions here"
            rows={4}
            className="mt-3"
            iconUrl={OrderNotes}
            iconColor="#10B981"
            iconBackground="#EAF7F1"
            onLocalInstructionFilesChange={onPendingInstructionFilesChange}
            onAttachmentRemove={async (att) => {
              const fid = String(att.file_id || "").trim();
              const oid = String(orderDocumentIdForAttachments || "").trim();
              if (!fid || !oid || !token) return;
              try {
                await deleteOrderDocumentFromOrder(token, oid, fid);
                onServerInstructionAttachmentRemoved?.();
              } catch (e) {
                showError(e instanceof Error ? e.message : "Failed to remove attachment");
              }
            }}
          />
        </div>
      </div>
      <div className="col-12 col-lg-6" id="row-overlap">
        <div className="card box-shadow-datatables shadow-border-2">
          <div className="ms-3 py-3">
            <h6 className="mb-0 order-tittle">Order Totals</h6>
          </div>
          <div className="card-body">
            <OrderDiscountCodeField
              className="mb-2"
              value={couponInput}
              appliedCode={appliedCouponCode}
              discountAmount={appliedCouponAmount}
              voucherApplied={voucherApplied}
              disabled={couponResolveDisabled}
              error={couponError}
              onChange={onCouponInputChange}
              onApply={onCouponApply}
              onClear={onCouponClear}
            />
            <hr />
            <div className="d-flex justify-content-between py-1">
              <span className="order-card-title">Sub Total:</span>
              <span className="order-card-cells">{money(subTotalAllBeforeDiscount)}</span>
            </div>
            {(appliedCouponAmount > 0 || appliedCouponCode) && (
              <div className="d-flex justify-content-between py-1">
                <span className="order-card-title">
                  Discount
                  {appliedCouponCode ? (
                    <>
                      :(
                      {appliedCouponAmount > 0 ? (
                        <span style={{ color: "#00a63e" }}>{appliedCouponCode}</span>
                      ) : (
                        appliedCouponCode
                      )}
                      )
                    </>
                  ) : (
                    ":"
                  )}
                </span>
                <span className="order-card-cells">
                  {appliedCouponAmount > 0 ? `-${money(appliedCouponAmount)}` : "$0.00"}
                </span>
              </div>
            )}
            <div className="d-flex justify-content-between py-1 align-items-start gap-2">
              <span className="order-card-title text-break" style={{ maxWidth: "62%" }}>
                {shippingChargeLabel}
              </span>
              {canEditShipping && isEditingShipping ? (
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: "100px" }}
                    value={manualShippingAmount}
                    onChange={(e) => onManualShippingAmountChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onSaveShipping();
                      if (e.key === "Escape") onCancelEditShipping();
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="btn btn-sm export-button br-8"
                    onClick={onSaveShipping}
                    title="Save"
                  >
                    <i className="ri-check-line fs-14" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm table-addbutton br-8"
                    onClick={onCancelEditShipping}
                    title="Cancel"
                  >
                    <i className="ri-close-line fs-14" />
                  </button>
                </div>
              ) : (
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <span className="order-card-cells">{money(shippingAmount)}</span>
                  {canEditShipping && (
                    <button
                      type="button"
                      className="btn btn-sm table-addbutton br-8"
                      onClick={onEditShipping}
                      title="Edit shipping charge"
                      style={{ lineHeight: 1 }}
                    >
                      <i className="ri-edit-line" />
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="d-flex justify-content-between py-1 align-items-start gap-2">
              <span
                className="order-card-title text-break"
                style={{ maxWidth: "62%" }}
                title={specialFeeBreakdownDisplay.tooltip}
              >
                {specialFeeBreakdownDisplay.labelText}:
              </span>
              <span className="order-card-cells flex-shrink-0">{money(specialFeeAmount)}</span>
            </div>
            <div className="py-1">
              <div className="d-flex justify-content-between align-items-center">
                <button
                  type="button"
                  onClick={onToggleTaxDetails}
                  className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
                  aria-expanded={showTaxDetails}
                  aria-controls="tax-breakdown"
                >
                  <span className="order-card-title">Tax :</span>
                </button>
                <span className="order-card-cells">
                  <span>{money(totalTaxAll)}</span>
                </span>
              </div>
            </div>

            <hr className="my-2" />
            <div className="d-flex justify-content-between pt-1">
              <strong className="order-card-title fs-19">Total (USD):</strong>
              <span className="order-card-cells fw-semibold">{money(grandTotal)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
